import { SelectInput } from '@/src/common/components/elements/Forms/SelectWithLabel'
import postFedexCheckQuery from '@/src/common/queries/PostFedexCheckQuery'
import { inputHasError } from '@/src/common/utilities/inputHasError'
import { FieldWrapper } from '@/src/modules/smarty/StyledComponents'
import { useEffect, useState } from 'react'
import { ClipLoader } from 'react-spinners'
import { FormErrorDisplay } from '../../build-a-card/FormErrorDisplay'
import { IDeliveryStreetAddress } from '../../cart/ICart'
import { LoadingText } from '../StyledComponents'
import AlertWithIcon from '@/src/common/components/elements/Alerts/AlertWithIcon'
import AddressNotEligibleForSingleShipContent from '@/src/modules/checkout/content/AddressNotEligibleForSingleShipContent'
import AddressHasNoShippingMethodsContent from '@/src/modules/checkout/content/AddressHasNoShippingMethodsContent'

const getDeliveryLabels = ({ data, options, allowUsps, allowFedex }: any) => {
    return options
        .filter((option: any) => {
            const revisedShippingName = option.delivery_option_name
                .replaceAll(' ', '-')
                .replace('1st', 'first')
                .toLowerCase()

            const isUspsEligible =
                allowUsps &&
                option?.carrier?.carrier_name.toLowerCase() === 'usps' &&
                revisedShippingName.includes('usps')

            const isFedexEligible =
                allowFedex &&
                option.carrier?.carrier_name.toLowerCase() === 'fedex' &&
                data?.includes(revisedShippingName)

            return isUspsEligible || isFedexEligible
        })
        .sort((a: any, b: any) => {
            const aVal = parseFloat(a.rate)
            const bVal = parseFloat(b.rate)
            return aVal - bVal
        })
        .map((deliveryOption: any) => {
            const {
                rate,
                delivery_option_name,
                delivery_option_eta,
                delivery_option_uuid,
            } = deliveryOption

            return {
                label: `$${parseFloat(rate).toFixed(
                    2
                )} ${delivery_option_name} (${delivery_option_eta})`,
                value: delivery_option_uuid,
            }
        })
}

export type TDeliveryMethodSelect = {
    address: IDeliveryStreetAddress
    options: any[]
    setIsMultipleShipments: Function
    clearAddress: Function
    hasOnlyBoxes: boolean
    selectedOption?: string
    allowUsps?: boolean
    allowFedex?: boolean
    formErrors?: any[]
    onChange?: Function
}

export default function DeliveryMethodSelect({
    address,
    selectedOption,
    setIsMultipleShipments,
    clearAddress,
    hasOnlyBoxes,
    options = [],
    allowUsps = true,
    allowFedex = true,
    formErrors = [],
    onChange = () => {},
}: TDeliveryMethodSelect) {
    const [deliveryOptions, setDeliveryOptions] = useState<any[]>(options)
    const [deliveryData, setDeliveryData] = useState<any>()
    const [loading, setLoading] = useState(false)
    const [debounceTimer, setDebounceTimer] = useState<any>()

    const { address1, city, state, zip } = address
    const isFullAddress = Boolean(
        address1 && city && state && zip.trim().length === 5
    )
    const [hasNoValidDeliveryOptions, setHasNoValidDeliveryOptions] =
        useState<boolean>(false)

    function clearAddressAndResetHasValid() {
        clearAddress()
        setHasNoValidDeliveryOptions(false)
    }

    const debounceUpdateOptions = (ms: number = 500) => {
        clearTimeout(debounceTimer)

        const timer = setTimeout(() => {
            if (
                address?.address1 &&
                address?.city &&
                address?.state &&
                address?.zip &&
                options.length
            ) {
                setLoading(true)

                postFedexCheckQuery({
                    companyName: '',
                    shippingName: address?.name ?? '',
                    addressLine1: address.address1,
                    addressLine2: address.address2 ?? '',
                    city: address.city,
                    stateAbbreviation: address.state,
                    zipCode: address.zip,
                })
                    .then((data: any) => {
                        const deliveryOptions = getDeliveryLabels({
                            data,
                            options,
                            allowUsps,
                            allowFedex,
                        })

                        setDeliveryOptions(deliveryOptions)
                        setDeliveryData(data)
                        setLoading(false)

                        if (!selectedOption && deliveryOptions[0]) {
                            // auto-select the first (cheapest) option
                            onChange(deliveryOptions[0].value)
                        }
                    })
                    .catch((e) => {
                        console.log(e)
                        setDeliveryOptions([])
                        setLoading(false)
                    })
            } else {
                setDeliveryOptions([])
            }
        }, ms)

        setDebounceTimer(timer)
    }

    useEffect(() => {
        // update delivery options when the address changes
        debounceUpdateOptions()
    }, [address])

    useEffect(() => {
        // when the options change, update the delivery option labels
        if (deliveryData) {
            const deliveryOptions = getDeliveryLabels({
                data: deliveryData,
                options,
                allowUsps,
                allowFedex,
            })

            setDeliveryOptions(deliveryOptions)
        } else if (options.length) {
            debounceUpdateOptions()
        }
    }, [options])

    useEffect(() => {
        setHasNoValidDeliveryOptions(
            isFullAddress && !deliveryOptions.length && !allowUsps
        )
    }, [allowUsps, deliveryOptions.length, isFullAddress])

    return (
        <>
            <FieldWrapper style={{ zIndex: 1, marginTop: 0 }}>
                {loading ? (
                    <LoadingText>
                        <p>Loading shipping methods</p>
                        <ClipLoader size={30} />
                    </LoadingText>
                ) : (
                    <SelectInput
                        label={'Shipping Method'}
                        placeholder={'Shipping Method'}
                        className={'shipping-method'}
                        value={selectedOption}
                        options={deliveryOptions}
                        fieldWrapperVariant={'full-width'}
                        hasError={inputHasError(formErrors, 'delivery')}
                        onValueChange={(x: string) => {
                            selectedOption = x
                            onChange(x)
                        }}
                    />
                )}

                <FormErrorDisplay formErrors={formErrors} path={'delivery'} />
            </FieldWrapper>
            {/* handles if no delivery options or military base*/}
            {hasNoValidDeliveryOptions && (
                <AlertWithIcon iconName={'warning'}>
                    {!options.length || hasOnlyBoxes ? (
                        <AddressHasNoShippingMethodsContent
                            clearAddress={clearAddressAndResetHasValid}
                        />
                    ) : (
                        <AddressNotEligibleForSingleShipContent
                            setIsMultipleShipments={setIsMultipleShipments}
                            clearAddress={clearAddressAndResetHasValid}
                        />
                    )}
                </AlertWithIcon>
            )}
        </>
    )
}
